import { ChangeEvent, JSX, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import FormHelperText from "@mui/material/FormHelperText";
import InputLabel from "@mui/material/InputLabel";

import DeleteIcon from "@mui/icons-material/Delete";
import UploadFileIcon from "@mui/icons-material/UploadFile";

import { FormField } from "lib/components/form/FormTypes";
import IconButton from "lib/ui/button/IconButton";

export type ImageUploadProps = {
	name?: string;
	label: string;
	field: FormField<string>;
	height: number;
	width: number;
	background?: string;
};

export default function ImageUpload(
	props: Readonly<ImageUploadProps>
): JSX.Element {
	const { t } = useTranslation();

	const [url, setUrl] = useState<string>(props.field.value);
	const [error, setError] = useState(props.field.error);

	useEffect(() => {
		if (props.field.value !== url) {
			setUrl(props.field.value);
		}
	}, [props.field.value]);

	useEffect(() => {
		if (props.field.error !== error) {
			setError(props.field.error);
		}
	}, [props.field.error]);

	const onSelectFile = useCallback(
		(event: ChangeEvent<HTMLInputElement>): void => {
			event.preventDefault();

			if (!event.target.files || event.target.files.length === 0) {
				return;
			}

			const file = event.target.files[0];

			const fileReader = new FileReader();
			fileReader.onload = (evt: ProgressEvent<FileReader>) => {
				if (typeof evt.target?.result !== "string") {
					return;
				}

				const img = document.createElement("img");
				img.onload = () => {
					const widthRatio = img.width / props.width;
					const heightRatio = img.height / props.height;
					const ratio = Math.max(widthRatio, heightRatio);

					const canvas = document.createElement("canvas");
					canvas.width = img.width / ratio;
					canvas.height = img.height / ratio;

					const ctx = canvas.getContext("2d");
					if (ctx === null) {
						return;
					}

					ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

					props.field.value = canvas
						.toDataURL(file.type)
						.split(",")[1];
					props.field.error = false;

					setUrl(props.field.value);
					setError(props.field.error);
				};

				img.src = evt.target.result;
			};

			fileReader.readAsDataURL(file);
		},
		[props.field]
	);

	const clearImage = useCallback(() => {
		props.field.value = "";
		props.field.error = false;

		setUrl("");
	}, [props.field]);

	function getImage(): JSX.Element {
		if (!url) {
			return (
				<UploadFileIcon
					color="disabled"
					sx={{ fontSize: `min(${props.height}, ${props.width})` }}
				/>
			);
		} else {
			return (
				<img
					src={`data:image;base64,${url}`}
					style={{
						height: "100%",
						maxWidth: "100%",
						objectFit: "contain",
						background: props.background
					}}
				/>
			);
		}
	}

	function getDeleteButton(): JSX.Element {
		if (!url) {
			return <></>;
		}

		return (
			<IconButton
				tooltip={t("delete")}
				icon={<DeleteIcon />}
				size="small"
				onClick={clearImage}
			/>
		);
	}

	return (
		<form
			style={{
				minHeight: props.height,
				minWidth: props.width
			}}>
			<div style={{ overflow: "hidden" }}>
				<label
					htmlFor={`upload-${props.label}`}
					style={{
						cursor: "pointer"
					}}>
					<InputLabel
						shrink={true}
						error={error}
						required={props.field.required}>
						{props.label}
					</InputLabel>
					<div
						style={{
							maxHeight: props.height,
							maxWidth: props.width,
							float: "left"
						}}>
						{getImage()}
					</div>
				</label>
				<div style={{ marginLeft: "4px", float: "left" }}>
					{getDeleteButton()}
				</div>
			</div>
			<input
				id={`upload-${props.label}`}
				type="file"
				hidden
				accept={".jpg, .png, .svg"}
				aria-invalid={error}
				required={props.field.required}
				onChange={onSelectFile}
			/>
			<FormHelperText error={error}>
				{t("fileFormat", {
					height: props.height,
					width: props.width
				})}
			</FormHelperText>
		</form>
	);
}
